import { BreakpointTrackerContext } from '@jutro/layout';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useDataRefresh, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { TranslatorContext } from '@jutro/locale';
import PropTypes from 'prop-types';
import _, { update } from 'lodash';
import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import metadata from './AdditionalInterestDetails.metadata.json5';
import messages from './AdditionalInterestDetails.messages.js';
import styles from '../AdditionalInterestComponent.module.scss';

function AdditionalInterestDetails(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const { item, saveData, cancelData, isInvalid, showErrors, onValidate } = props;
    const {
        isComponentValid,
        onValidate: setComponentValidation,
    } = useValidation('AdditionalInterestDetails');

    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useContext(TranslatorContext);
    const [additionalInfoVM, setAdditionalInfoVM] = useState(item);
    const isUSZIPCode = (code) => /^[0-9]{5}(?:-[0-9]{4})?$/.test(code);
    const [zipcodeValidationMessage, setZIPCodeValidationMessage] = useState([]);
    const { refreshData } = useDataRefresh();
    const [displayError, setDisplayError] = useState(false);
    const errorHandling = useCallback(() => {
        if (!displayError) { setDisplayError(true) }
    }, [displayError]);

    useEffect(() => {
        if (onValidate && !_.isNull(isComponentValid)) {
            onValidate(isComponentValid, 'AdditionalInterestDetails');
        }
    }, [isComponentValid, onValidate]);

    useEffect(() => {
        isInvalid(isComponentValid, 'AdditionalInterestDetails');
    }, [isInvalid, isComponentValid])

    const getAvailableMortgageValues = useMemo(() => {
        const availableMortgageValues = viewModelService.productMetadata.get('pc').types.getTypelist('MortgagePositionType_ext').codes.map((code) => {
            return {
                code: code.code,
                name: translator({ id: code.name })
            }
        })

        return availableMortgageValues
    }, [viewModelService, translator]);


    const validateZIPCode = useCallback(
        (value, path) => {
            _.set(additionalInfoVM, path, value);
            setZIPCodeValidationMessage([]);

            if (!value) {
                setZIPCodeValidationMessage([translator(messages.Required)])
            } else if (!isUSZIPCode(value)) {
                setZIPCodeValidationMessage([translator(messages.ZIPCodeValidationMessage)])
            }

            setAdditionalInfoVM(additionalInfoVM);
        },
        [additionalInfoVM, translator]);

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        name: {
            visible: additionalInfoVM?.policyAdditionalInterest.subtype.value === 'Company'
        },
        loanNumber: {
            path: 'contractNumber',
            required: true,
            showRequired: true
        },
        mortgagePosition: {
            path: 'mortgagePosition_ext',
            availableValues: getAvailableMortgageValues,
            required: true,
            showRequired: true
        },
        zipCode: {
            validationMessages: zipcodeValidationMessage
        },
    };


    const saveAdditionalInterest = () => isComponentValid ? saveData(additionalInfoVM) : errorHandling();

    const cancelAdditionalInterest = () => {
        isInvalid(true, 'AdditionalInterestDetails');
        cancelData();
    }

    const writeValue = useCallback(
        (value, path) => {

            _.set(additionalInfoVM, `${path}.value`, value);
            setAdditionalInfoVM(additionalInfoVM);
            refreshData();
        },
        [additionalInfoVM, refreshData]
    );

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onSave: saveAdditionalInterest,
            onCancel: cancelAdditionalInterest,
            OnvalidateZIPCode: validateZIPCode
        },
    };


    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={additionalInfoVM}
            overrideProps={overrideProps}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            onValueChange={writeValue}
            onValidationChange={setComponentValidation}
            showErrors={showErrors || displayError}
        />
    );
}

AdditionalInterestDetails.propTypes = {
    item: PropTypes.shape({}).isRequired,
    saveData: PropTypes.func.isRequired
};

export default AdditionalInterestDetails;
